<template>
  <div class="messages-container">
    <div class="messages-content">
      <div class="messages-header">
        <div class="messages-tabs">
          <div 
            v-for="tab in tabs" 
            :key="tab.value" 
            class="tab-item"
            :class="{ active: activeTab === tab.value }"
            @click="handleTabClick(tab.value)"
          >
            {{ tab.label }}
          </div>
        </div>
      </div>
      
      <!-- 消息列表 -->
      <div v-loading="messagesLoading" class="messages-list">
        <div 
          v-for="message in filteredMessages" 
          :key="message.id" 
          class="message-item"
          @click="openMessage(message)"
        >
          <!-- 左侧状态点 -->
          <div :class="['message-status-dot', { unread: !message.read }]"></div>
          
          <!-- 中间内容 -->
          <div class="message-text">
            {{ message.title }}
          </div>
          
          <!-- 右侧时间和删除 -->
          <div class="message-right">
            <span class="message-time">{{ message.time }}</span>
            <i class="el-icon-delete delete-icon" @click.stop="deleteMessage(message.id)"></i>
          </div>
        </div>
        <!-- 无数据展位图 -->
        <div v-if="filteredMessages.length === 0 && !messagesLoading" class="no-messages">
          <el-empty description="暂无消息" />
        </div>
      </div>
      
      <!-- 分页控件 -->
      <div class="pagination">
        <el-pagination
          :current-page="messagesQuery.pageNum"
          :page-size="messagesQuery.pageSize"
          :total="messagesTotal"
          layout="prev, pager, next"
          prev-text="上一页"
          next-text="下一页"
          @current-change="handleCurrentChange"
        ></el-pagination>
      </div>
    </div>
  </div>
</template>

<script>
import { getMyMessages, markMyMessageRead } from '@/api/authApi'

export default {
  name: 'AppMessages',
  created() {
    this.loadMessages()
  },
  data() {
    return {
      // 标签页数据
      tabs: [
        { label: '全部通知', value: 'all' },
        { label: '课程提醒', value: 'course_reminder' },
        { label: '开课提醒', value: 'course_start' },
        { label: '订单通知', value: 'order' }
      ],
      activeTab: 'all',
      
      messagesLoading: false,
      messagesTotal: 0,
      messagesQuery: {
        pageNum: 1,
        pageSize: 8
      },
      messages: []
    }
  },
  computed: {
    filteredMessages() {
      if (this.activeTab === 'all') {
        return this.messages
      }
      return this.messages.filter(msg => {
        const rawType = msg.raw && (msg.raw.messageType ?? msg.raw.type)
        return rawType === this.activeTab
      })
    }
  },
  methods: {
    extractListAndTotal(data) {
      const list =
        (Array.isArray(data) && data) ||
        (data && Array.isArray(data.list) && data.list) ||
        (data && Array.isArray(data.records) && data.records) ||
        (data && Array.isArray(data.items) && data.items) ||
        []

      const total =
        (data && (data.total ?? data.count ?? data.totalCount)) ||
        (typeof data === 'object' && !Array.isArray(data) ? list.length : list.length)

      return { list, total: Number(total) || 0 }
    },
    normalizeMessage(item, index) {
      const id = (item && (item.id ?? item.messageId)) ?? index
      const content =
        (item && (item.messageContent ?? item.content ?? item.title ?? item.text)) || ''
      const messageType = item && (item.messageType ?? item.type)
      const createTime = String((item && (item.sendTime ?? item.createTime ?? item.updateTime)) || '')

      const readRaw = item && (item.readStatus ?? item.readCount ?? item.read_count ?? item.read)
      const read =
        readRaw === 1 ||
        readRaw === '1' ||
        readRaw === true ||
        (typeof readRaw === 'number' && readRaw > 0)

      const titlePrefix = messageType && messageType !== 'all' ? `${messageType} | ` : ''

      return {
        id,
        title: `${titlePrefix}${String(content || '')}`,
        time: createTime,
        content: String(content || ''),
        read,
        raw: item
      }
    },
    async loadMessages() {
      if (this.messagesLoading) return
      this.messagesLoading = true
      try {
        const params = {
          pageNum: this.messagesQuery.pageNum,
          pageSize: this.messagesQuery.pageSize
        }
        if (this.activeTab && this.activeTab !== 'all') params.messageType = this.activeTab

        const res = await getMyMessages(params)
        const data = res && res.data
        const { list, total } = this.extractListAndTotal(data)
        this.messages = list.map((item, index) => this.normalizeMessage(item, index))
        this.messagesTotal = total
      } catch (err) {
        this.messages = []
        this.messagesTotal = 0
        this.$message.error((err && err.message) || '获取站内信失败')
      } finally {
        this.messagesLoading = false
      }
    },
    handleCurrentChange(page) {
      this.messagesQuery.pageNum = page
      this.loadMessages()
    },
    handleTabClick(tabValue) {
      this.activeTab = tabValue
      this.messagesQuery.pageNum = 1
      this.loadMessages()
    },
    async openMessage(message) {
      if (!message) return
      try {
        if (message.id && !message.read) {
          await markMyMessageRead(message.id)
          this.messages = this.messages.map(m => (m.id === message.id ? { ...m, read: true } : m))
          window.dispatchEvent(new CustomEvent('message-read'))
        }
      } catch (err) {
        void err
      }

      const content = String(message.content || message.title || '')
      if (!content) return
      try {
        await this.$alert(content, '站内信', {
          confirmButtonText: '知道了'
        })
      } catch (err) {
        void err
      }
    },
    deleteMessage(id) {
      this.$confirm('确认删除该消息吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.messages = this.messages.filter(m => m.id !== id)
        window.dispatchEvent(new CustomEvent('message-read'))
        this.$message({
          type: 'success',
          message: '删除成功!'
        });
      }).catch(() => {});
    }
  }
}
</script>

<style scoped lang="scss">
.messages-container {
  padding: 0;
  background: #fff;
  min-height: 500px;
}

.messages-content {
  background-color: #fff;
  padding: 0;
}

/* 顶部Header样式 */
.messages-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0 20px 0;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 10px;
}

.messages-tabs {
  display: flex;
}

.tab-item {
  padding: 0 20px;
  cursor: pointer;
  font-size: 16px;
  color: #606266;
  position: relative;
  
  &:first-child {
    padding-left: 0;
  }
  
  &:hover {
    color: #3E2CCD;
  }
  
  &.active {
    color: #3E2CCD;
    font-weight: 600;
  }
}

/* 消息列表样式 */
.messages-list {
  margin-bottom: 20px;
}

.message-item {
  display: flex;
  align-items: center;
  padding: 20px 0;
  border-bottom: 1px dashed #e0e0e0;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background-color: #fcfcfc;
  }
}

.message-status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #dcdfe6;
  margin-right: 15px;
  flex-shrink: 0;
}

.message-status-dot.unread {
  background-color: #3E2CCD;
}

.message-text {
  flex: 1;
  font-size: 14px;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding-right: 20px;
}

.message-right {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.message-time {
  font-size: 14px;
  color: #909399;
  margin-right: 20px;
}

.delete-icon {
  font-size: 16px;
  color: #909399;
  cursor: pointer;
  transition: color 0.2s;
  
  &:hover {
    color: #f56c6c;
  }
}

/* 分页样式 */
.pagination {
  display: flex;
  justify-content: center;
  margin-top: 30px;
  padding-bottom: 20px;
}

::v-deep .el-pagination {
  .btn-prev, .btn-next {
    background-color: #fff;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    padding: 0 10px;
    height: 32px;
    line-height: 32px;
    
    span {
      font-size: 13px;
    }
  }
  
  .el-pager li {
    background: #fff;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    margin: 0 5px;
    font-weight: normal;
    height: 32px;
    line-height: 32px;
    
    &.active {
      background-color: #3E2CCD;
      border-color: #3E2CCD;
      color: #fff;
    }
    
    &:hover {
      color: #3E2CCD;
    }
  }
}
</style>
